import React from 'react'
import { Stack, Typography } from '@mui/material'
import {
  CampaignSnsRequirement,
  CampaignSnsRequirementSnsType,
  ReviewSection,
  ReviewTarget,
  FormatVersion,
  LanguageType,
} from '~/generated/graphql'
import { useFormContext, useWatch } from 'react-hook-form'
import { SnsIcon } from '~/components/common/SnsIcon'
import { Campaign_SNS_LABEL } from '~/constants/snstype'
import { CAMPAIGN_FORM_TITLE } from '~/constants/campaign'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { LanguageSpecificTagsInput } from './LanguageSpecificTagsInput'
import { z } from 'zod'
import { desiredLanguagesSchemaFields } from './DesiredLanguages'
import { UnsetAlert } from '~/components/common/UnsetAlert'

export const tagsSchemaFields = {
  campaignSnsRequirements: z
    .array(
      z.object({
        snsType: z.nativeEnum(CampaignSnsRequirementSnsType),
        tagList: z.array(
          z.object({
            languageType: z.nativeEnum(LanguageType),
            tags: z.array(z.string()),
          }),
        ),
        requestUrl: z.string().max(255, { message: 'リクエストURLは255文字以内で入力してください' }),
      }),
    )
    .min(1, { message: '少なくとも1つのSNS要件が必要です' }),
  ...desiredLanguagesSchemaFields,
}
export const tagsSchema = z.object(tagsSchemaFields)

type Props = CampaignInputComponentProps<{
  campaignSnsRequirements: readonly CampaignSnsRequirement[]
}>

export const Tags = ({ campaignSnsRequirements, description }: Props) => {
  const {
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
    control,
  } = useFormContext<z.input<typeof tagsSchema>>()

  const desiredLanguages = useWatch({ name: 'desiredLanguages', control })

  // 中国語はサポートしていないので、中国語のみ選択されている場合は項目ごと表示しない
  if (desiredLanguages.length > 0 && desiredLanguages.every((lang) => lang === LanguageType.Chinese)) {
    return null
  }

  return (
    <CampaignInputComponent
      title={CAMPAIGN_FORM_TITLE.hashTagSetup}
      description={description}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.TagList, ReviewTarget.LegalTagList],
      }}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      <Stack gap={5}>
        {campaignSnsRequirements.map((e, index) => {
          const tagList = getValues(`campaignSnsRequirements.${index}.tagList`)

          const handleTagListChange = (updatedTagList: typeof tagList) => {
            setValue(`campaignSnsRequirements.${index}.tagList`, updatedTagList)
          }

          const handleError = (errorMessage?: string) => {
            if (errorMessage) {
              setError(`campaignSnsRequirements.${index}.tagList`, { message: errorMessage })
            } else {
              clearErrors('campaignSnsRequirements')
            }
          }

          return (
            <Stack key={e.snsType} gap={2}>
              <Stack spacing={0.5} direction="row" alignItems="center">
                <SnsIcon snsType={e.snsType} size={32} />
                <Typography fontWeight="bold">
                  {e.snsType === CampaignSnsRequirementSnsType.NoSettings || Campaign_SNS_LABEL[e.snsType]}
                </Typography>
              </Stack>

              <LanguageSpecificTagsInput
                snsType={e.snsType}
                tagList={tagList}
                errorMessage={errors.campaignSnsRequirements?.[index]?.tagList?.message}
                onChange={handleTagListChange}
                onError={handleError}
                desiredLanguages={desiredLanguages}
              />

              {desiredLanguages.length === 0 && (
                <UnsetAlert>
                  <Typography variant="body2">投稿希望言語を設定してください。</Typography>
                </UnsetAlert>
              )}
            </Stack>
          )
        })}
      </Stack>
    </CampaignInputComponent>
  )
}
